import React, { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import Toast from '@/components/Toast';
import ConfirmModal from '@/components/ConfirmModal';
import { dictionaryAdminApi } from '@/features/admin/dictionaryAdminApi';
import { DictionaryEntry, WordDictionary } from '@/types/wordDictionary';

const PARTS_OF_SPEECH = [
  'noun',
  'verb',
  'adjective',
  'adverb',
  'pronoun',
  'preposition',
  'conjunction',
  'interjection',
  'article',
  'determiner',
  'phrase',
];

type AudioConfirmState = {
  word: string;
  variant: 'UK' | 'US';
} | null;

type AudioDeleteConfirmState = {
  word: string;
  variant: 'UK' | 'US';
  partOfSpeech?: string | null;
} | null;

export const DictionaryReview: React.FC = () => {
  const [entries, setEntries] = useState<WordDictionary[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<WordDictionary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchWord, setSearchWord] = useState('');
  const [activeSearchWord, setActiveSearchWord] = useState('');
  const [filterTerm, setFilterTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'unreviewed'>('unreviewed');
  const [selectedWords, setSelectedWords] = useState<Set<string>>(new Set());
  const [editingWords, setEditingWords] = useState<Set<string>>(new Set());
  const [editingData, setEditingData] = useState<Map<string, Partial<WordDictionary>>>(new Map());
  const [savingWords, setSavingWords] = useState<Set<string>>(new Set());
  const [regeneratingDictionaryWords, setRegeneratingDictionaryWords] = useState<Set<string>>(
    new Set()
  );
  const [regeneratingAudio, setRegeneratingAudio] = useState<Set<string>>(new Set());
  const [deletingAudio, setDeletingAudio] = useState<Set<string>>(new Set());
  const recorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const [currentRecordingKey, setCurrentRecordingKey] = useState<string | null>(null);
  const [recordedUrls, setRecordedUrls] = useState<Record<string, string>>({});
  const [recordedBase64, setRecordedBase64] = useState<Record<string, string>>({});
  const [regeneratedAudioUrls, setRegeneratedAudioUrls] = useState<Record<string, string>>({});
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [audioConfirm, setAudioConfirm] = useState<AudioConfirmState>(null);
  const [audioDeleteConfirm, setAudioDeleteConfirm] = useState<AudioDeleteConfirmState>(null);
  const notifTimeout = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    },
    []
  );

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    duration = 3000
  ) => {
    setNotification({ message, type });
    if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    notifTimeout.current = window.setTimeout(() => setNotification(null), duration);
  };

  const fetchEntries = async (search?: string): Promise<void> => {
    setIsLoading(true);
    try {
      const resolvedFilterType = search ? 'all' : filterType;
      const data = await dictionaryAdminApi.getAll({
        filterType: resolvedFilterType,
        search,
      });
      const items = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data.data)
          ? data.data
          : Array.isArray(data.data?.items)
            ? data.data.items
            : [];
      setEntries(items as WordDictionary[]);
      showNotification('Dictionary entries loaded', 'success');
    } catch (error) {
      showNotification(error instanceof Error ? error.message : 'Failed to fetch entries', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (): Promise<void> => {
    const normalizedWord = searchWord.trim().toLowerCase();
    setSelectedWords(new Set());

    // Disable search all by default, as it can be expensive and is not the common use case
    // if (!normalizedWord) {
    //   setActiveSearchWord('');
    //   await fetchEntries();
    //   return;
    // }

    setActiveSearchWord(normalizedWord);
    await fetchEntries(normalizedWord);
  };

  useEffect(() => {
    if (activeSearchWord) {
      fetchEntries(activeSearchWord);
      return;
    }

    // Disable search all by default, as it can be expensive and is not the common use case
    // fetchEntries();
  }, [filterType]);

  useEffect(() => {
    let filtered = entries;
    if (filterTerm.trim()) {
      const lower = filterTerm.toLowerCase();
      filtered = filtered.filter(
        e =>
          e.word.toLowerCase().includes(lower) ||
          e.entries?.some(
            s =>
              (s.meaningVN || '').toLowerCase().includes(lower) ||
              (s.exampleEN || '').toLowerCase().includes(lower)
          )
      );
    }
    setFilteredEntries(filtered);
  }, [entries, filterTerm]);

  const handleSelectWord = (word: string) => {
    const next = new Set(selectedWords);
    if (next.has(word)) next.delete(word);
    else next.add(word);
    setSelectedWords(next);
  };

  const handleSelectAll = () => {
    if (selectedWords.size === filteredEntries.length && selectedWords.size > 0)
      setSelectedWords(new Set());
    else setSelectedWords(new Set(filteredEntries.map(e => e.word)));
  };

  const handleEditEntry = (entry: WordDictionary) => {
    setEditingWords(prev => new Set(prev).add(entry.word));
    setEditingData(prev => new Map(prev).set(entry.word, { ...entry }));
  };

  const createEmptyMeaning = (): DictionaryEntry => ({
    partOfSpeech: '',
    ipaUS: '',
    ipaUK: '',
    meaningVN: '',
    exampleEN: '',
    exampleVN: '',
    audioUS: null,
    audioUK: null,
  });

  const handleAddMeaning = (entry: WordDictionary) => {
    setEditingWords(prev => new Set(prev).add(entry.word));
    setEditingData(prev => {
      const updated = new Map(prev);
      const existing = (updated.get(entry.word) as Partial<WordDictionary>) || entry;
      const entriesCopy = Array.isArray(existing.entries) ? [...existing.entries] : [];
      entriesCopy.unshift(createEmptyMeaning());

      updated.set(entry.word, {
        ...existing,
        word: entry.word,
        entries: entriesCopy,
      });

      return updated;
    });
    showNotification('New meaning added. Fill fields and click Save.', 'info');
  };

  const handleDeleteMeaning = (word: string, index: number): void => {
    setEditingData(prev => {
      const updated = new Map(prev);
      const current = updated.get(word) as Partial<WordDictionary> | undefined;
      if (!current || !Array.isArray(current.entries)) return prev;

      const nextEntries = current.entries.filter((_, idx) => idx !== index);

      updated.set(word, {
        ...current,
        entries: nextEntries,
      });

      return updated;
    });

    showNotification('Entry removed. Click Save to persist changes.', 'info');
  };

  const handleCancelEdit = (word: string) => {
    setEditingWords(prev => {
      const s = new Set(prev);
      s.delete(word);
      return s;
    });
    setEditingData(prev => {
      const m = new Map(prev);
      m.delete(word);
      return m;
    });
  };

  // (removed unused) field-level edits are handled via sub-field helpers

  const handleSubFieldChange = (
    word: string,
    subIndex: number,
    field: keyof NonNullable<WordDictionary['entries']>[number],
    value: string
  ) => {
    setEditingData(prev => {
      const updated = new Map(prev);
      const current = (updated.get(word) as Partial<WordDictionary>) || {};
      const entriesCopy = Array.isArray(current.entries)
        ? [...current.entries]
        : current.entries
          ? [...current.entries]
          : [];
      while (entriesCopy.length <= subIndex) entriesCopy.push({} as any);
      const sub = { ...(entriesCopy[subIndex] as any) };
      (sub as any)[field] = value;
      entriesCopy[subIndex] = sub as any;
      updated.set(word, { ...current, entries: entriesCopy });
      return updated;
    });
  };

  const getEditingSubValue = (
    entry: WordDictionary,
    idx: number,
    field: keyof NonNullable<WordDictionary['entries']>[number]
  ) => {
    if (!editingWords.has(entry.word)) return (entry.entries?.[idx]?.[field] as string) ?? '';
    const edited = editingData.get(entry.word) as Partial<WordDictionary> | undefined;
    return (
      (edited?.entries?.[idx]?.[field] as string) ?? (entry.entries?.[idx]?.[field] as string) ?? ''
    );
  };

  const getDisplayEntries = (entry: WordDictionary): DictionaryEntry[] => {
    if (!isEditing(entry.word)) {
      return Array.isArray(entry.entries) ? entry.entries : [];
    }

    const edited = editingData.get(entry.word) as Partial<WordDictionary> | undefined;
    return Array.isArray(edited?.entries)
      ? (edited.entries as DictionaryEntry[])
      : Array.isArray(entry.entries)
        ? entry.entries
        : [];
  };

  const isMeaningRowEmpty = (entry?: Partial<DictionaryEntry>): boolean => {
    if (!entry) return true;

    const textFields = [
      entry.partOfSpeech,
      entry.ipaUS,
      entry.ipaUK,
      entry.meaningVN,
      entry.exampleEN,
      entry.exampleVN,
    ];

    const hasAnyText = textFields.some(value => (value || '').trim().length > 0);
    const hasAnyAudio = Boolean(entry.audioUS || entry.audioUK);

    return !hasAnyText && !hasAnyAudio;
  };

  const validateBeforeSave = (word: string, edited: Partial<WordDictionary>): string[] => {
    const errors: string[] = [];
    const draftEntries = Array.isArray(edited.entries) ? edited.entries : [];

    if (draftEntries.length === 0) {
      errors.push('At least one meaning is required.');
      return errors;
    }

    const originalEntry = entries.find(item => item.word === word);
    const originalCount = Array.isArray(originalEntry?.entries)
      ? originalEntry!.entries!.length
      : 0;

    draftEntries.forEach((meaning, index) => {
      const row = index + 1;
      const isNewRow = index >= originalCount;

      if (isMeaningRowEmpty(meaning)) {
        errors.push(`Meaning ${row} is empty. Please fill it or remove it before saving.`);
        return;
      }

      if (isNewRow) {
        const partOfSpeech = (meaning.partOfSpeech || '').trim();
        const meaningVN = (meaning.meaningVN || '').trim();

        if (!partOfSpeech) {
          errors.push(`Meaning ${row}: Part of speech is required.`);
        }

        if (!meaningVN) {
          errors.push(`Meaning ${row}: Meaning (VN) is required.`);
        }
      }
    });

    return errors;
  };

  const handleSaveEntry = async (word: string) => {
    const edited = editingData.get(word);
    if (!edited) return;

    const validationErrors = validateBeforeSave(word, edited);
    if (validationErrors.length > 0) {
      showNotification(validationErrors[0], 'error', 5000);
      return;
    }

    setSavingWords(prev => new Set(prev).add(word));
    try {
      const returned = await dictionaryAdminApi.update(word, edited as WordDictionary);
      setEntries(prev => prev.map(e => (e.word === returned.word ? returned : e)));
      handleCancelEdit(word);
      showNotification('Entry saved successfully', 'success');
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to save entry', 'error');
    } finally {
      setSavingWords(prev => {
        const s = new Set(prev);
        s.delete(word);
        return s;
      });
    }
  };

  const handleRegenerateAudio = async (
    word: string,
    variant: 'UK' | 'US',
    options?: { partOfSpeech?: string | null; ipa?: string; previewKey?: string }
  ) => {
    const audioKey = `${word}-${variant}`;
    setRegeneratingAudio(prev => new Set(prev).add(audioKey));
    try {
      const result = await dictionaryAdminApi.regenerateAudio(word, variant, options);
      const audioUrl = result?.data?.audioUrl || result?.audioUrl;

      if (audioUrl && options?.previewKey) {
        setRegeneratedAudioUrls(prev => ({ ...prev, [options.previewKey as string]: audioUrl }));
      }

      showNotification(`Audio ${variant} regenerated successfully`, 'success');
    } catch (err) {
      showNotification(
        err instanceof Error ? err.message : `Failed to regenerate audio ${variant}`,
        'error'
      );
    } finally {
      setRegeneratingAudio(prev => {
        const s = new Set(prev);
        s.delete(audioKey);
        return s;
      });
      setAudioConfirm(null);
    }
  };

  const handleDeleteAudio = async (
    word: string,
    variant: 'UK' | 'US',
    partOfSpeech?: string | null
  ): Promise<void> => {
    const audioKey = `${word}-${variant}`;
    setDeletingAudio(prev => new Set(prev).add(audioKey));
    try {
      await dictionaryAdminApi.deleteAudio(word, variant, partOfSpeech);
      const updatedEntry = await dictionaryAdminApi.getByWord(word);
      setEntries(prev => prev.map(e => (e.word === word ? updatedEntry.data || updatedEntry : e)));
      showNotification(`Audio ${variant} deleted successfully`, 'success');
    } catch (err) {
      showNotification(
        err instanceof Error ? err.message : `Failed to delete audio ${variant}`,
        'error'
      );
    } finally {
      setDeletingAudio(prev => {
        const s = new Set(prev);
        s.delete(audioKey);
        return s;
      });
      setAudioDeleteConfirm(null);
    }
  };

  const handleRegenerateDictionary = async (word: string): Promise<void> => {
    setRegeneratingDictionaryWords(prev => new Set(prev).add(word));
    try {
      const result = await dictionaryAdminApi.regenerateDictionary(word);
      const updated = result?.data?.entry || result?.entry;

      if (!updated || !updated.word) {
        throw new Error('Dictionary regeneration returned invalid response');
      }

      setEntries(prev => prev.map(e => (e.word === word ? updated : e)));
      showNotification(`Dictionary regenerated for "${word}"`, 'success');
    } catch (err) {
      showNotification(
        err instanceof Error ? err.message : 'Failed to regenerate dictionary',
        'error'
      );
    } finally {
      setRegeneratingDictionaryWords(prev => {
        const s = new Set(prev);
        s.delete(word);
        return s;
      });
    }
  };

  const makeKey = (word: string, idx: number, variant: 'UK' | 'US') =>
    `${word}::${idx}::${variant}`;

  const startRecording = async (word: string, idx: number, variant: 'UK' | 'US') => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showNotification('Media devices not supported in this browser', 'error');
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const options: MediaRecorderOptions = { mimeType: 'audio/webm' };
      const mr = new MediaRecorder(stream, options);
      recordingChunksRef.current = [];
      mr.ondataavailable = e => {
        if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blob = new Blob(recordingChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          const key = makeKey(word, idx, variant);
          setRecordedUrls(prev => ({ ...prev, [key]: url }));
          setRecordedBase64(prev => ({ ...prev, [key]: base64 }));
        };
        reader.readAsDataURL(blob);
        // stop tracks
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
        }
        recorderRef.current = null;
        setCurrentRecordingKey(null);
      };
      recorderRef.current = mr;
      setCurrentRecordingKey(makeKey(word, idx, variant));
      mr.start();
      showNotification('Recording started', 'info');
    } catch (err) {
      showNotification('Failed to start recording', 'error');
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
      showNotification('Recording stopped', 'info');
    }
  };

  const discardRecording = (word: string, idx: number, variant: 'UK' | 'US') => {
    const key = makeKey(word, idx, variant);
    setRecordedUrls(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    setRecordedBase64(prev => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
    showNotification('Recording discarded', 'info');
  };

  const saveRecording = async (
    word: string,
    idx: number,
    variant: 'UK' | 'US',
    partOfSpeech?: string
  ) => {
    const key = makeKey(word, idx, variant);
    const base64 = recordedBase64[key];
    if (!base64) return showNotification('No recording to save', 'info');
    try {
      setRegeneratingAudio(prev => new Set(prev).add(`${word}-${variant}`));
      await dictionaryAdminApi.uploadAudio(word, variant, base64, partOfSpeech);
      // Refresh entry from server
      const updatedEntry = await dictionaryAdminApi.getByWord(word);
      setEntries(prev => prev.map(e => (e.word === word ? updatedEntry.data || updatedEntry : e)));
      // clear stored recording for this key
      discardRecording(word, idx, variant);
      showNotification('Audio saved successfully', 'success');
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to save audio', 'error');
    } finally {
      setRegeneratingAudio(prev => {
        const s = new Set(prev);
        s.delete(`${word}-${variant}`);
        return s;
      });
    }
  };

  const uploadFileAndSave = async (word: string, idx: number, variant: 'UK' | 'US') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/*,audio/mpeg';
    input.onchange = () => {
      const file = input.files && input.files[0];
      if (!file) return showNotification('No file selected', 'info');
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        const key = makeKey(word, idx, variant);
        const url = URL.createObjectURL(file);
        setRecordedUrls(prev => ({ ...prev, [key]: url }));
        setRecordedBase64(prev => ({ ...prev, [key]: base64 }));
        setCurrentRecordingKey(null);
        showNotification('Audio file loaded (click Save to persist)', 'success');
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const handleApproveWords = async () => {
    if (selectedWords.size === 0) {
      showNotification('Please select words to approve', 'info');
      return;
    }
    setIsLoading(true);
    try {
      await dictionaryAdminApi.approve(Array.from(selectedWords));
      setEntries(prev =>
        prev.map(e => (selectedWords.has(e.word) ? { ...e, isReviewed: true } : e))
      );
      setSelectedWords(new Set());
      setShowApproveConfirm(false);
      showNotification('Words approved', 'success');
    } catch (err) {
      showNotification(err instanceof Error ? err.message : 'Failed to approve words', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // getEntryValue removed — not used in current UI

  const speakWord = (word: string, lang: string) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
    try {
      const utter = new SpeechSynthesisUtterance(word);
      utter.lang = lang;
      const voices = window.speechSynthesis.getVoices();
      const preferred =
        voices.find(v => v.lang && v.lang.toLowerCase().includes(lang.toLowerCase())) ||
        voices.find(v => v.lang && v.lang.startsWith('en'));
      if (preferred) utter.voice = preferred;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    } catch (e) {}
  };

  const isEditing = (word: string) => editingWords.has(word);
  const isSaving = (word: string) => savingWords.has(word);
  const isRegeneratingDictionary = (word: string) => regeneratingDictionaryWords.has(word);
  const isRegeneratingAudio = (word: string, variant: 'UK' | 'US') =>
    regeneratingAudio.has(`${word}-${variant}`);
  const isDeletingAudio = (word: string, variant: 'UK' | 'US') =>
    deletingAudio.has(`${word}-${variant}`);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Dictionary Review</h1>

        {notification && (
          <Toast notification={notification} onClose={() => setNotification(null)} />
        )}

        <Card className="mb-6">
          <div className="space-y-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search by word
                </label>
                <input
                  type="text"
                  placeholder="Enter a word"
                  value={searchWord}
                  onChange={e => setSearchWord(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleSearch();
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <Button type="button" variant="primary" onClick={() => void handleSearch()}>
                Search
              </Button>
            </div>

            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by word or definition
                </label>
                <input
                  type="text"
                  placeholder="Filter..."
                  value={filterTerm}
                  onChange={e => setFilterTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="w-60">
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter</label>
                <select
                  value={filterType}
                  onChange={e => setFilterType(e.target.value as 'all' | 'unreviewed')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="all">All Words</option>
                  <option value="unreviewed">Unreviewed Only</option>
                </select>
              </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={
                      selectedWords.size === filteredEntries.length && selectedWords.size > 0
                    }
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Select All ({filteredEntries.length})
                  </span>
                </label>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  disabled={selectedWords.size === 0}
                  onClick={() => setShowApproveConfirm(true)}
                >
                  Approve ({selectedWords.size})
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <div className="mb-4 text-sm text-gray-600">
          Showing {filteredEntries.length} of {entries.length} entries
          {activeSearchWord ? ` for search "${activeSearchWord}"` : ''}
        </div>

        {isLoading ? (
          <Card className="text-center py-8">
            <p className="text-gray-500">Loading...</p>
          </Card>
        ) : filteredEntries.length === 0 ? (
          <Card className="text-center py-8">
            <p className="text-gray-500">No entries found</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredEntries.map(entry => (
              <Card key={entry.word} className="hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4 p-4">
                  <input
                    type="checkbox"
                    checked={selectedWords.has(entry.word)}
                    onChange={() => handleSelectWord(entry.word)}
                    className="w-4 h-4 rounded border-gray-300 mt-1"
                  />

                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">
                          {entry.word}{' '}
                          {Array.isArray(entry.entries) && entry.entries.length > 0 ? (
                            <span className="text-sm font-normal text-gray-500">
                              ({entry.entries.length} meaning{entry.entries.length > 1 ? 's' : ''})
                            </span>
                          ) : null}
                        </h3>
                        <p className="text-xs text-gray-500">
                          Updated:{' '}
                          {entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        {isEditing(entry.word) ? (
                          <>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => handleSaveEntry(entry.word)}
                              loading={isSaving(entry.word)}
                              disabled={isSaving(entry.word)}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCancelEdit(entry.word)}
                              disabled={isSaving(entry.word)}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => void handleRegenerateDictionary(entry.word)}
                              loading={isRegeneratingDictionary(entry.word)}
                              disabled={isRegeneratingDictionary(entry.word)}
                            >
                              Re-generate Dict
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleEditEntry(entry)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAddMeaning(entry)}
                            >
                              Add meaning
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 space-y-3">
                      {getDisplayEntries(entry).length > 0 ? (
                        getDisplayEntries(entry).map((sub, idx) => (
                          <div
                            key={`${entry.word}-sub-${idx}`}
                            className="p-3 border rounded grid grid-cols-1 md:grid-cols-2 gap-4"
                          >
                            <div>
                              {isEditing(entry.word) ? (
                                <div className="space-y-2 text-sm">
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">
                                      Part of speech
                                    </label>
                                    <select
                                      value={getEditingSubValue(entry, idx, 'partOfSpeech')}
                                      onChange={e =>
                                        handleSubFieldChange(
                                          entry.word,
                                          idx,
                                          'partOfSpeech',
                                          e.target.value
                                        )
                                      }
                                      className="w-48 px-2 py-1 border rounded"
                                    >
                                      <option value="">Select...</option>
                                      {PARTS_OF_SPEECH.map(pos => (
                                        <option key={pos} value={pos}>
                                          {pos}
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">
                                      IPA US
                                    </label>
                                    <input
                                      type="text"
                                      value={getEditingSubValue(entry, idx, 'ipaUS')}
                                      onChange={e =>
                                        handleSubFieldChange(
                                          entry.word,
                                          idx,
                                          'ipaUS',
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-2 py-1 border rounded"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">
                                      IPA UK
                                    </label>
                                    <input
                                      type="text"
                                      value={getEditingSubValue(entry, idx, 'ipaUK')}
                                      onChange={e =>
                                        handleSubFieldChange(
                                          entry.word,
                                          idx,
                                          'ipaUK',
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-2 py-1 border rounded"
                                    />
                                  </div>

                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">
                                      Audio (US)
                                    </label>
                                    {sub.audioUS ? (
                                      <div className="flex items-center gap-3">
                                        <audio controls src={sub.audioUS} className="h-8" />
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() =>
                                            setAudioDeleteConfirm({
                                              word: entry.word,
                                              variant: 'US',
                                              partOfSpeech: sub.partOfSpeech,
                                            })
                                          }
                                          loading={isDeletingAudio(entry.word, 'US')}
                                          disabled={isDeletingAudio(entry.word, 'US')}
                                        >
                                          Delete US audio
                                        </Button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        aria-label="Read aloud US"
                                        className="text-primary-600 hover:text-primary-800 focus:outline-none shrink-0"
                                        onClick={() => speakWord(entry.word, 'en-US')}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                          width="18"
                                          height="18"
                                        >
                                          <path d="M9.25 4.75A.75.75 0 0 1 10 5.5v9a.75.75 0 0 1-1.28.53L5.81 12H3.5A1.5 1.5 0 0 1 2 10.5v-3A1.5 1.5 0 0 1 3.5 6h2.31l2.91-3.03A.75.75 0 0 1 10 3.5v1.25Zm4.03 1.22a.75.75 0 0 1 1.06 1.06A5.5 5.5 0 0 1 16.5 10a5.5 5.5 0 0 1-2.16 4.22.75.75 0 1 1-.94-1.18A4 4 0 0 0 15 10a4 4 0 0 0-1.6-3.18.75.75 0 0 1-.12-1.06Zm-2.01 2.01a.75.75 0 0 1 1.06 1.06A2.5 2.5 0 0 1 13 10a2.5 2.5 0 0 1-.67 1.71.75.75 0 1 1-1.12-1A1 1 0 0 0 12 10a1 1 0 0 0-.27-.69.75.75 0 0 1 0-1.06Z" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>

                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">
                                      Audio (UK)
                                    </label>
                                    {sub.audioUK ? (
                                      <div className="flex items-center gap-3">
                                        <audio controls src={sub.audioUK} className="h-8" />
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() =>
                                            setAudioDeleteConfirm({
                                              word: entry.word,
                                              variant: 'UK',
                                              partOfSpeech: sub.partOfSpeech,
                                            })
                                          }
                                          loading={isDeletingAudio(entry.word, 'UK')}
                                          disabled={isDeletingAudio(entry.word, 'UK')}
                                        >
                                          Delete UK audio
                                        </Button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        aria-label="Read aloud UK"
                                        className="text-primary-600 hover:text-primary-800 focus:outline-none shrink-0"
                                        onClick={() => speakWord(entry.word, 'en-GB')}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                          width="18"
                                          height="18"
                                        >
                                          <path d="M9.25 4.75A.75.75 0 0 1 10 5.5v9a.75.75 0 0 1-1.28.53L5.81 12H3.5A1.5 1.5 0 0 1 2 10.5v-3A1.5 1.5 0 0 1 3.5 6h2.31l2.91-3.03A.75.75 0 0 1 10 3.5v1.25Zm4.03 1.22a.75.75 0 0 1 1.06 1.06A5.5 5.5 0 0 1 16.5 10a5.5 5.5 0 0 1-2.16 4.22.75.75 0 1 1-.94-1.18A4 4 0 0 0 15 10a4 4 0 0 0-1.6-3.18.75.75 0 0 1-.12-1.06Zm-2.01 2.01a.75.75 0 0 1 1.06 1.06A2.5 2.5 0 0 1 13 10a2.5 2.5 0 0 1-.67 1.71.75.75 0 1 1-1.12-1A1 1 0 0 0 12 10a1 1 0 0 0-.27-.69.75.75 0 0 1 0-1.06Z" />
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="space-y-2 text-sm">
                                  <div className="text-sm text-gray-600 mb-2">
                                    <strong>{sub.partOfSpeech || `Entry ${idx + 1}`}</strong>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {!sub.audioUS && (
                                      <button
                                        type="button"
                                        aria-label="Read aloud US"
                                        className="text-primary-600 hover:text-primary-800 focus:outline-none shrink-0"
                                        onClick={() => speakWord(entry.word, 'en-US')}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                          width="18"
                                          height="18"
                                        >
                                          <path d="M9.25 4.75A.75.75 0 0 1 10 5.5v9a.75.75 0 0 1-1.28.53L5.81 12H3.5A1.5 1.5 0 0 1 2 10.5v-3A1.5 1.5 0 0 1 3.5 6h2.31l2.91-3.03A.75.75 0 0 1 10 3.5v1.25Zm4.03 1.22a.75.75 0 0 1 1.06 1.06A5.5 5.5 0 0 1 16.5 10a5.5 5.5 0 0 1-2.16 4.22.75.75 0 1 1-.94-1.18A4 4 0 0 0 15 10a4 4 0 0 0-1.6-3.18.75.75 0 0 1-.12-1.06Zm-2.01 2.01a.75.75 0 0 1 1.06 1.06A2.5 2.5 0 0 1 13 10a2.5 2.5 0 0 1-.67 1.71.75.75 0 1 1-1.12-1A1 1 0 0 0 12 10a1 1 0 0 0-.27-.69.75.75 0 0 1 0-1.06Z" />
                                        </svg>
                                      </button>
                                    )}
                                    <span className="text-xs text-gray-600 w-28">IPA US:</span>
                                    <span className="text-gray-600">{sub.ipaUS || 'N/A'}</span>
                                  </div>
                                  {sub.audioUS && (
                                    <div className="flex items-center gap-3">
                                      <audio controls src={sub.audioUS} className="h-8" />
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                          setAudioDeleteConfirm({
                                            word: entry.word,
                                            variant: 'US',
                                            partOfSpeech: sub.partOfSpeech,
                                          })
                                        }
                                        loading={isDeletingAudio(entry.word, 'US')}
                                        disabled={isDeletingAudio(entry.word, 'US')}
                                      >
                                        Delete US audio
                                      </Button>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2">
                                    {!sub.audioUK && (
                                      <button
                                        type="button"
                                        aria-label="Read aloud UK"
                                        className="text-primary-600 hover:text-primary-800 focus:outline-none shrink-0"
                                        onClick={() => speakWord(entry.word, 'en-GB')}
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          viewBox="0 0 20 20"
                                          fill="currentColor"
                                          width="18"
                                          height="18"
                                        >
                                          <path d="M9.25 4.75A.75.75 0 0 1 10 5.5v9a.75.75 0 0 1-1.28.53L5.81 12H3.5A1.5 1.5 0 0 1 2 10.5v-3A1.5 1.5 0 0 1 3.5 6h2.31l2.91-3.03A.75.75 0 0 1 10 3.5v1.25Zm4.03 1.22a.75.75 0 0 1 1.06 1.06A5.5 5.5 0 0 1 16.5 10a5.5 5.5 0 0 1-2.16 4.22.75.75 0 1 1-.94-1.18A4 4 0 0 0 15 10a4 4 0 0 0-1.6-3.18.75.75 0 0 1-.12-1.06Zm-2.01 2.01a.75.75 0 0 1 1.06 1.06A2.5 2.5 0 0 1 13 10a2.5 2.5 0 0 1-.67 1.71.75.75 0 1 1-1.12-1A1 1 0 0 0 12 10a1 1 0 0 0-.27-.69.75.75 0 0 1 0-1.06Z" />
                                        </svg>
                                      </button>
                                    )}
                                    <span className="text-xs text-gray-600 w-28">IPA UK:</span>
                                    <span className="text-gray-600">{sub.ipaUK || 'N/A'}</span>
                                  </div>
                                  {sub.audioUK && (
                                    <div className="flex items-center gap-3">
                                      <audio controls src={sub.audioUK} className="h-8" />
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                          setAudioDeleteConfirm({
                                            word: entry.word,
                                            variant: 'UK',
                                            partOfSpeech: sub.partOfSpeech,
                                          })
                                        }
                                        loading={isDeletingAudio(entry.word, 'UK')}
                                        disabled={isDeletingAudio(entry.word, 'UK')}
                                      >
                                        Delete UK audio
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            <div>
                              {isEditing(entry.word) ? (
                                <div>
                                  <div className="mb-2">
                                    <label className="block text-xs text-gray-600 mb-1">
                                      Meaning (VN)
                                    </label>
                                    <input
                                      type="text"
                                      value={getEditingSubValue(entry, idx, 'meaningVN')}
                                      onChange={e =>
                                        handleSubFieldChange(
                                          entry.word,
                                          idx,
                                          'meaningVN',
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-2 py-1 border rounded"
                                    />
                                  </div>
                                  <div className="mb-2">
                                    <label className="block text-xs text-gray-600 mb-1">
                                      Example (EN)
                                    </label>
                                    <textarea
                                      value={getEditingSubValue(entry, idx, 'exampleEN')}
                                      onChange={e =>
                                        handleSubFieldChange(
                                          entry.word,
                                          idx,
                                          'exampleEN',
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-2 py-1 border rounded h-20"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-gray-600 mb-1">
                                      Example (VN)
                                    </label>
                                    <textarea
                                      value={getEditingSubValue(entry, idx, 'exampleVN')}
                                      onChange={e =>
                                        handleSubFieldChange(
                                          entry.word,
                                          idx,
                                          'exampleVN',
                                          e.target.value
                                        )
                                      }
                                      className="w-full px-2 py-1 border rounded h-20"
                                    />
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="mb-2">
                                    <p className="font-medium text-gray-700 mb-1">Meaning (VN)</p>
                                    <p className="text-gray-600">{sub.meaningVN || 'N/A'}</p>
                                  </div>
                                  <div className="mb-2">
                                    <p className="font-medium text-gray-700 mb-1">Example (EN)</p>
                                    <p className="text-gray-600">{sub.exampleEN || 'N/A'}</p>
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-700 mb-1">Example (VN)</p>
                                    <p className="text-gray-600">{sub.exampleVN || 'N/A'}</p>
                                  </div>
                                </div>
                              )}
                            </div>
                            {/* Footer row for variant controls (Re-play / Re-record / Save) */}
                            {isEditing(entry.word) && (
                              <div className="col-span-2 gap-2 mt-2">
                                {/* US controls */}
                                <div className="mb-5">
                                  <p className="font-medium text-gray-700 mb-1">Update US audio</p>
                                  {(() => {
                                    const keyUS = makeKey(entry.word, idx, 'US');
                                    if (currentRecordingKey === keyUS) {
                                      return (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => stopRecording()}
                                        >
                                          Stop recording
                                        </Button>
                                      );
                                    }

                                    if (recordedUrls[keyUS]) {
                                      return (
                                        <div className="flex items-center gap-2">
                                          <div className="flex items-center gap-3">
                                            <audio
                                              controls
                                              src={recordedUrls[keyUS]}
                                              className="h-8"
                                            />
                                          </div>

                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => startRecording(entry.word, idx, 'US')}
                                          >
                                            Re-record US
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => uploadFileAndSave(entry.word, idx, 'US')}
                                          >
                                            Upload audio file
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={() =>
                                              saveRecording(entry.word, idx, 'US', sub.partOfSpeech)
                                            }
                                            loading={isRegeneratingAudio(entry.word, 'US')}
                                          >
                                            Save US audio
                                          </Button>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div className="space-y-2">
                                        {regeneratedAudioUrls[keyUS] && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-green-700 font-medium">
                                              New generated audio:
                                            </span>
                                            <audio
                                              controls
                                              src={regeneratedAudioUrls[keyUS]}
                                              className="h-8"
                                            />
                                          </div>
                                        )}
                                        <div className="flex items-center gap-2">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => uploadFileAndSave(entry.word, idx, 'US')}
                                          >
                                            Upload mp3 file
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => startRecording(entry.word, idx, 'US')}
                                          >
                                            Record audio
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() =>
                                              void handleRegenerateAudio(entry.word, 'US', {
                                                partOfSpeech: sub.partOfSpeech,
                                                ipa: getEditingSubValue(entry, idx, 'ipaUS'),
                                                previewKey: keyUS,
                                              })
                                            }
                                            loading={isRegeneratingAudio(entry.word, 'US')}
                                            disabled={isRegeneratingAudio(entry.word, 'US')}
                                          >
                                            Re-generate audio
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>

                                {/* UK controls */}
                                <div>
                                  <p className="font-medium text-gray-700 mb-1">Update UK audio</p>

                                  {(() => {
                                    const keyUK = makeKey(entry.word, idx, 'UK');
                                    if (currentRecordingKey === keyUK) {
                                      return (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => stopRecording()}
                                        >
                                          Stop recording
                                        </Button>
                                      );
                                    }

                                    if (recordedUrls[keyUK]) {
                                      return (
                                        <div className="flex items-center gap-2">
                                          <div className="flex items-center gap-3">
                                            <audio
                                              controls
                                              src={recordedUrls[keyUK]}
                                              className="h-8"
                                            />
                                          </div>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => startRecording(entry.word, idx, 'UK')}
                                          >
                                            Re-record UK
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => uploadFileAndSave(entry.word, idx, 'UK')}
                                          >
                                            Upload audio file
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="primary"
                                            onClick={() =>
                                              saveRecording(entry.word, idx, 'UK', sub.partOfSpeech)
                                            }
                                            loading={isRegeneratingAudio(entry.word, 'UK')}
                                          >
                                            Save UK audio
                                          </Button>
                                        </div>
                                      );
                                    }

                                    return (
                                      <div className="space-y-2">
                                        {regeneratedAudioUrls[keyUK] && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-xs text-green-700 font-medium">
                                              New generated audio:
                                            </span>
                                            <audio
                                              controls
                                              src={regeneratedAudioUrls[keyUK]}
                                              className="h-8"
                                            />
                                          </div>
                                        )}
                                        <div className="flex items-center gap-2">
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => uploadFileAndSave(entry.word, idx, 'UK')}
                                          >
                                            Upload mp3 file
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => startRecording(entry.word, idx, 'UK')}
                                          >
                                            Record audio
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() =>
                                              void handleRegenerateAudio(entry.word, 'UK', {
                                                partOfSpeech: sub.partOfSpeech,
                                                ipa: getEditingSubValue(entry, idx, 'ipaUK'),
                                                previewKey: keyUK,
                                              })
                                            }
                                            loading={isRegeneratingAudio(entry.word, 'UK')}
                                            disabled={isRegeneratingAudio(entry.word, 'UK')}
                                          >
                                            Re-generate audio
                                          </Button>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>

                                <div className="mt-4 flex justify-end">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-red-300 text-red-600 hover:bg-red-50"
                                    onClick={() => handleDeleteMeaning(entry.word, idx)}
                                  >
                                    Delete entry
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500">No sub-entries available</div>
                      )}
                    </div>

                    <div className="mt-3 pt-3 border-t flex justify-between items-center text-xs text-gray-500">
                      <span>
                        {entry.isReviewed ? (
                          <span className="text-green-600 font-medium">✓ Reviewed</span>
                        ) : (
                          <span className="text-yellow-600 font-medium">⚠ Unreviewed</span>
                        )}
                      </span>
                      <span>
                        Updated:{' '}
                        {entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4" />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showApproveConfirm && (
        <ConfirmModal
          open={true}
          title="Approve Words"
          description={`Are you sure you want to approve ${selectedWords.size} word(s)?`}
          onConfirm={handleApproveWords}
          onCancel={() => setShowApproveConfirm(false)}
          confirmText="Approve"
          isLoading={isLoading}
        />
      )}

      {audioConfirm && (
        <ConfirmModal
          open={true}
          title="Re-generate Audio"
          description={`Are you sure you want to re-generate audio ${audioConfirm.variant} for "${audioConfirm.word}"? This will delete the existing audio file and generate a new one.`}
          onConfirm={() => handleRegenerateAudio(audioConfirm.word, audioConfirm.variant)}
          onCancel={() => setAudioConfirm(null)}
          confirmText="Re-generate"
          isLoading={isRegeneratingAudio(audioConfirm.word, audioConfirm.variant)}
        />
      )}

      {audioDeleteConfirm && (
        <ConfirmModal
          open={true}
          title="Delete Audio"
          description={`Are you sure you want to delete ${audioDeleteConfirm.variant} audio for "${audioDeleteConfirm.word}"?`}
          onConfirm={() =>
            handleDeleteAudio(
              audioDeleteConfirm.word,
              audioDeleteConfirm.variant,
              audioDeleteConfirm.partOfSpeech
            )
          }
          onCancel={() => setAudioDeleteConfirm(null)}
          confirmText="Delete"
          isLoading={isDeletingAudio(audioDeleteConfirm.word, audioDeleteConfirm.variant)}
        />
      )}
    </div>
  );
};

export default DictionaryReview;
